import { generateId } from "@msuite/utils/strings";
import { cva, type VariantProps } from "class-variance-authority";
import { useState } from "react";
import { Button } from "../button";
import { AlertIcon, Icon as LucideIcon } from "../icons";
import { TiptapDisplay } from "../libs/tiptap";
import { AlertLabel } from "./alert-label";

/** Props Interface */
interface AlertPropsBase extends Omit<VariantProps<typeof alertContainer>, "onClick"> {
	title: string;
	description: string;
	isLoading?: boolean;
	onClick?: () => void;
	isDismissable?: boolean;
	dismissKey?: string;
	mentionOptions?: {
		projects: Array<any> | undefined;
		members: Array<any> | undefined;
		mentionRenderers: any;
	};
}

interface AlertPropsDismissable extends AlertPropsBase {
	isDismissble: true;
	dismissKey: `${string}-alert`;
}

interface AlertPropsNotDismissable extends AlertPropsBase {
	isDismissable?: false;
	dismissKey?: never;
}

type AlertProps = AlertPropsDismissable | AlertPropsNotDismissable;

function Alert({
	variant,
	spacing,
	title,
	description,
	isLoading,
	onClick,
	isDismissable,
	dismissKey,
	mentionOptions,
}: AlertProps) {
	/** State */
	const [_, setRenderId] = useState<string>(generateId());

	/** Props */
	const classes = alertContainer({ variant, onClick: !!onClick, spacing });

	if (isDismissable) {
		const isDismissed = localStorage.getItem(dismissKey);
		if (isDismissed) return null;
	}

	function handleOnDismiss() {
		if (!dismissKey) return;
		localStorage.setItem(dismissKey, "true");
		setRenderId(generateId());
	}

	/** Render */
	return (
		<div
			className={classes}
			style={{
				gridTemplateColumns: "max-content 1fr auto",
			}}
			onClick={onClick}
		>
			<div className="flex-center col-start-1 row-span-2">
				<AlertIcon
					isLoading={isLoading}
					variant={variant}
				/>
			</div>
			<p className="col-start-2 text-base font-bold">{title}</p>
			<p className="col-start-2 opacity-80 line-clamp-3">
				{mentionOptions ? (
					<TiptapDisplay
						json={description}
						{...mentionOptions}
					/>
				) : (
					description
				)}
			</p>
			{isDismissable && (
				<div className="v-stack row-start-1 row-span-2 col-start-3 flex-center">
					<Button
						variant="ghost"
						stopPropagation
						icon={<LucideIcon name="X" />}
						onClick={handleOnDismiss}
					/>
				</div>
			)}
		</div>
	);
}

const alertContainer = cva(["card text-sm ring-4 grid"], {
	variants: {
		variant: {
			success: ["border-emerald-500 ring-emerald-500/10", "dark:bg-emerald-950/30"],
			danger: ["border-red-500 ring-red-500/10", "dark:bg-red-950/30"],
			warning: ["border-amber-500 ring-amber-500/10", "dark:bg-amber-950/30"],
			info: ["border-blue-500 ring-blue-500/10", "dark:bg-blue-950/30"],
			neutral: ["border-color ring-neutral-500/10", "dark:bg-neutral-950/30"],
		},
		spacing: {
			compact: ["px-3 py-2 gap-x-3 gap-y-0"],
			normal: ["px-4 py-3 gap-x-4 gap-y-0.5"],
		},
		onClick: {
			true: ["clickable-opacity clickable-scale"],
			false: ["cursor-default"],
		},
	},
	defaultVariants: {
		spacing: "compact",
		variant: "neutral",
	},
});

Alert.Icon = AlertIcon;
Alert.Label = AlertLabel;

export { Alert };
